'use client'

import { useAtom, useAtomValue } from 'jotai'
import { useParams } from 'next/navigation'

import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'

import Dialog from '@/app/[locale]/[ferry]/Dialog'
import { readdDepartureWithoutOffer } from '@/app/[locale]/[ferry]/yourbooking/_actions/readdDepartureWithoutOffer'
import { selectedDateAtom, selectedDeparturesAtom, selectedOfferAtom, selectedPetsAtom, selectedRoutesAtom } from '@/states/selectionAtom'

import { getUrlLocale } from '@/utils'

import { useTranslation } from '@/hooks/useDebugTranslations'

interface Props {
  basket: BffInterfacesBasket.Basket
  onClose: () => void
  show: boolean
  updateBasket: any
}

export const RemoveOfferModal = ({ basket, onClose, show, updateBasket }: Props) => {
  const { t } = useTranslation()
  const params = useParams()

  const [selectedDepartures, setSelectedDepartures] = useAtom(selectedDeparturesAtom)
  const [selectedOffer, setSelectedOffer] = useAtom(selectedOfferAtom)
  const [selectedRoutes, setSelectedRoutes] = useAtom(selectedRoutesAtom)
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom)
  const { showNumberOfPets } = useAtomValue(selectedPetsAtom)

  return (
    <Dialog
      dataAttr="offer-remove"
      description={t('OFFER-MODAL-BODY-REMOVE')}
      headline={t('OFFER-MODAL-TITLE-REMOVE-NEW', { offer: selectedOffer.offerTitle })}
      onPrimaryButtonClicked={async () => {
        if (selectedOffer.offerRLSHours && selectedDepartures && selectedDepartures.departureOut?.offer) {
          selectedDate &&
            setSelectedDate({
              outboundDepartureDate: selectedDate.outboundDepartureDate,
              returnDepartureDate: null,
            })
          selectedRoutes &&
            setSelectedRoutes({
              homebound: null,
              outbound: selectedRoutes.outbound,
            })
          const readdError = await readdDepartureWithoutOffer(basket.id, getUrlLocale())
          if (readdError) {
            updateBasket()
            onClose() // close the dialog so the user isn't stranded when the re-add fails
            return
          }
          // Close the dialog before clearing state: the atoms persist in sessionStorage across the hard
          // navigation below, so the offer must be cleared here, but doing it while the dialog is still
          // open would flash an empty offer title in the header.
          onClose()
          setSelectedDepartures({
            departureHome: null,
            departureOut: { ...selectedDepartures.departureOut, departurePrice: basket.departures[0].ticketPrice, offer: undefined },
          })
          setSelectedOffer({})
          window.location.href = `/${params?.locale}/${params?.ferry}/${showNumberOfPets > 0 ? 'pets' : 'extras'}?basketId=${basket.id}`
        }
      }}
      onRequestClose={onClose}
      onSecondaryButtonClicked={onClose}
      primaryButtonText={t('OFFER-MODAL-REMOVE')}
      secondaryButtonText={t('CANCEL')}
      show={show}
    />
  )
}
